'use client'

/**
 * Client-side enhancer for `<Carousel>`. Tiny on purpose — one
 * `useEffect`, no state library, no third-party gesture lib (native
 * scroll-snap handles touch out of the box).
 *
 * What it does:
 *
 *   - Reads `data-carousel-track` / `data-carousel-slide` /
 *     `data-carousel-dot` / `data-carousel-prev` / `data-carousel-next`
 *     from the SSR markup. This component never renders — it only
 *     hydrates behavior — so the carousel works without JS, and JS
 *     just makes it nicer.
 *
 *   - Autoplay: when `config.autoplay`, advances every `interval_ms`.
 *     Pauses on focus-within, hover, and `prefers-reduced-motion`.
 *     If `loop=false`, stops at the last slide instead of wrapping.
 *
 *   - Dot/arrow click → smooth scrollTo the corresponding slide.
 *
 *   - Keyboard arrow nav when the carousel region has focus.
 *
 *   - IntersectionObserver tracks the active slide so dot
 *     `aria-selected` and `tabIndex` mirror reality.
 */

import { useEffect, useRef } from 'react'

export type CarouselClientConfig = {
  autoplay: boolean
  interval_ms: number
  loop: boolean
  pagination: 'dots' | 'lines' | 'thumbnails' | 'arrows' | 'none'
}

export type CarouselClientProps = {
  config: CarouselClientConfig
  slideCount: number
}

export function CarouselClient({ config, slideCount }: CarouselClientProps): null {
  const cleanupRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    // CarouselClient renders null, so we can't climb from its own DOM
    // node. Instead query for any unhydrated carousel regions on the
    // page and tag each as we wire it. This handles multiple carousels
    // per page correctly (each region gets its own listeners).
    const regions = Array.from(
      document.querySelectorAll<HTMLElement>(
        '[data-component="Carousel"]:not([data-carousel-hydrated])',
      ),
    )
    if (regions.length === 0) return
    const cleanups: Array<() => void> = []
    for (const r of regions) {
      r.setAttribute('data-carousel-hydrated', 'true')
      cleanups.push(hydrateOne(r, config, slideCount))
    }
    cleanupRef.current = () => {
      for (const c of cleanups) c()
    }
    return cleanupRef.current
    // Intentionally only re-run when these primitives change — config is
    // a fresh object each render but its primitive fields are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.autoplay, config.interval_ms, config.loop, config.pagination, slideCount])

  return null
}

function hydrateOne(
  region: HTMLElement,
  config: CarouselClientConfig,
  slideCount: number,
): () => void {
  const track = region.querySelector<HTMLElement>('[data-carousel-track]')
  const slides = Array.from(region.querySelectorAll<HTMLElement>('[data-carousel-slide]'))
  const dots = Array.from(region.querySelectorAll<HTMLElement>('[data-carousel-dot]'))
  const prevBtn = region.querySelector<HTMLElement>('[data-carousel-prev]')
  const nextBtn = region.querySelector<HTMLElement>('[data-carousel-next]')
  if (!track || slides.length === 0) return () => {}

  let active = 0
  let autoplayTimer: ReturnType<typeof setInterval> | null = null
  let paused = false

  function scrollToSlide(i: number): void {
    const target = slides[i]
    if (!target) return
    track!.scrollTo({ left: target.offsetLeft - track!.offsetLeft, behavior: 'smooth' })
  }

  function setActive(i: number): void {
    active = i
    for (let k = 0; k < dots.length; k++) {
      const dot = dots[k]
      if (k === i) {
        dot.setAttribute('aria-selected', 'true')
        dot.setAttribute('tabindex', '0')
      } else {
        dot.setAttribute('aria-selected', 'false')
        dot.setAttribute('tabindex', '-1')
      }
    }
  }

  function next(): void {
    const last = slideCount - 1
    if (active < last) scrollToSlide(active + 1)
    else if (config.loop) scrollToSlide(0)
  }

  function prev(): void {
    if (active > 0) scrollToSlide(active - 1)
    else if (config.loop) scrollToSlide(slideCount - 1)
  }

  // Track the active slide via IntersectionObserver — most-visible wins.
  const io = new IntersectionObserver(
    (entries) => {
      let bestIndex = active
      let bestRatio = 0
      for (const entry of entries) {
        if (entry.intersectionRatio > bestRatio) {
          bestRatio = entry.intersectionRatio
          const idx = slides.indexOf(entry.target as HTMLElement)
          if (idx >= 0) bestIndex = idx
        }
      }
      if (bestIndex !== active) setActive(bestIndex)
    },
    { root: track, threshold: [0.25, 0.5, 0.75, 1] },
  )
  for (const s of slides) io.observe(s)

  // Click handlers.
  const dotHandlers: Array<() => void> = []
  for (let i = 0; i < dots.length; i++) {
    const handler = () => scrollToSlide(i)
    dots[i].addEventListener('click', handler)
    dotHandlers.push(handler)
  }
  const prevHandler = () => prev()
  const nextHandler = () => next()
  prevBtn?.addEventListener('click', prevHandler)
  nextBtn?.addEventListener('click', nextHandler)

  // Keyboard nav.
  function onKey(ev: KeyboardEvent): void {
    if (ev.key === 'ArrowLeft') {
      ev.preventDefault()
      prev()
    } else if (ev.key === 'ArrowRight') {
      ev.preventDefault()
      next()
    }
  }
  region.addEventListener('keydown', onKey)

  // Pause on hover / focus-within.
  function onPointerEnter(): void {
    paused = true
  }
  function onPointerLeave(): void {
    paused = false
  }
  region.addEventListener('pointerenter', onPointerEnter)
  region.addEventListener('pointerleave', onPointerLeave)
  region.addEventListener('focusin', onPointerEnter)
  region.addEventListener('focusout', onPointerLeave)

  // Autoplay, gated on prefers-reduced-motion.
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (config.autoplay && !reducedMotion) {
    autoplayTimer = setInterval(() => {
      if (!paused) next()
    }, config.interval_ms)
  }

  return () => {
    io.disconnect()
    for (let i = 0; i < dotHandlers.length; i++) {
      dots[i].removeEventListener('click', dotHandlers[i])
    }
    prevBtn?.removeEventListener('click', prevHandler)
    nextBtn?.removeEventListener('click', nextHandler)
    region.removeEventListener('keydown', onKey)
    region.removeEventListener('pointerenter', onPointerEnter)
    region.removeEventListener('pointerleave', onPointerLeave)
    region.removeEventListener('focusin', onPointerEnter)
    region.removeEventListener('focusout', onPointerLeave)
    if (autoplayTimer !== null) clearInterval(autoplayTimer)
    region.removeAttribute('data-carousel-hydrated')
  }
}

